/**
 * Cuộn ngang mượt bằng requestAnimationFrame — dùng cho carousel/listing row.
 *
 * Tạm tắt scroll-snap trong lúc animate; trả về hàm cancel nếu cần.
 */

const easeOutCubic = (progress) => 1 - (1 - progress) ** 3;

/**
 * Cuộn phần tử theo trục ngang tới vị trí targetLeft.
 * @param {HTMLElement|null} element
 * @param {number} targetLeft
 * @param {Object} [options]
 * @param {number} [options.duration=480]
 * @param {Function} [options.onComplete]
 * @returns {Function|undefined} Hàm hủy animation
 */
export const smoothScrollHorizontal = (element, targetLeft, options = {}) => {
  const { duration = 480, onComplete } = options;

  if (!element) {
    return undefined;
  }

  const startLeft = element.scrollLeft;
  const maxScroll = element.scrollWidth - element.clientWidth;
  const clampedTarget = Math.max(0, Math.min(targetLeft, maxScroll));
  const distance = clampedTarget - startLeft;

  if (Math.abs(distance) < 1) {
    onComplete?.();
    return undefined;
  }

  const previousSnapType = element.style.scrollSnapType;
  element.style.scrollSnapType = 'none';

  const startTime = performance.now();
  let animationFrameId = 0;

  const step = (currentTime) => {
    const progress = Math.min((currentTime - startTime) / duration, 1);
    element.scrollLeft = startLeft + distance * easeOutCubic(progress);

    if (progress < 1) {
      animationFrameId = requestAnimationFrame(step);
      return;
    }

    element.style.scrollSnapType = previousSnapType;
    onComplete?.();
  };

  animationFrameId = requestAnimationFrame(step);

  return () => {
    cancelAnimationFrame(animationFrameId);
    element.style.scrollSnapType = previousSnapType;
  };
};

/**
 * Tính vị trí scrollLeft cho lật trang carousel theo hướng.
 * @param {HTMLElement|null} element
 * @param {number} direction - 1 (next) hoặc -1 (prev)
 * @param {number} itemStep - Chiều rộng một item
 * @returns {number}
 */
export const getPagedScrollTarget = (element, direction, itemStep) => {
  if (!element || !direction) {
    return 0;
  }

  const maxScroll = element.scrollWidth - element.clientWidth;
  const visibleItems = Math.max(1, Math.floor(element.clientWidth / itemStep));
  const pageDistance = visibleItems * itemStep;
  const target = element.scrollLeft + direction * pageDistance;

  return Math.max(0, Math.min(target, maxScroll));
};
